//components/auth/auth-context.tsx
"use client"

import { createContext, useContext, useState, useCallback, useEffect } from "react"
import type { ReactNode } from "react"
import { 
  setAccessToken,
  clearTokens,
  getUserFromToken,
  refreshAccessToken,
  getAccessToken,
  isAccessTokenExpired, 
} from "@/lib/auth"
import io from "socket.io-client"
import { debugLog } from "@/lib/debug";
import { env } from "@/lib/env";



interface User {
  id: string
  email: string
  role: string
  tier: string
  energyBalance: number,
  balanceUsd: number
}

// Тип ответа от refreshAccessToken (чтобы не плодить any)
interface RefreshResponse {
  accessToken?: string
  user?: User
}

interface AuthContextType {
  user: User | null
  isAuthenticated: boolean
  isLoading: boolean
  logout: () => Promise<void>
  refreshUser: () => Promise<void>
}

// В самом верху auth-context.tsx
const API_URL = env.apiUrl;

const PRIVATE_PREFIXES = ['/chat', '/ops', '/billing', '/finance', '/ai', '/settings', '/profile', '/crm', '/calendar'];
function isPrivatePath(): boolean {
  if (typeof window === 'undefined') return false;
  const path = window.location.pathname;
  return PRIVATE_PREFIXES.some(p => path.startsWith(p));
}


    async function fetchUserFromApi(token: string): Promise<User | null> {
      try {
        const res = await fetch(`${API_URL}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` },
          credentials: "include",
        })
        if (!res.ok) return null
        return await res.json()
      } catch {
        return null
      }
    }



const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [ready, setReady] = useState(false)

  
    useEffect(() => {
      // BFCache защита
      const t0 = performance.now();
      const runId = Math.random().toString(36).slice(2, 7);
      debugLog('AUTH', 'useEffect start', { runId })
           const onPageShow = (e: PageTransitionEvent) => {
              if (!e.persisted) return;  // обычная загрузка — игнорируем
              const tok = getAccessToken();
              // Из BFCache: токена нет ИЛИ он истёк, И мы на приватной странице → /signin
              if ((!tok || isAccessTokenExpired(tok)) && isPrivatePath()) {
                clearTokens();
                window.location.replace('/signin?reason=expired');
              }
            };
            window.addEventListener('pageshow', onPageShow);

          // Синхронизация логаута между вкладками
          //const channel = new BroadcastChannel('auth');
          //  channel.onmessage = (e) => {
          //  if (e.data === 'logout' && !getAccessToken()) {
          //        window.location.replace('/signin');
          //      }
          //    };
          
          const existingToken = getAccessToken()
          const tokenExpired = existingToken ? isAccessTokenExpired(existingToken) : true
          debugLog('AUTH', 'mount', {
            runId,
            existingToken: existingToken ? 'EXISTS' : 'NULL',
            expired: tokenExpired,
            t: (performance.now()-t0).toFixed(0)
          })

          if (existingToken && !tokenExpired) {
            const decodedUser = getUserFromToken()
            if (decodedUser) {
              debugLog('AUTH', 'setUser from token', { runId, userId: decodedUser.id, t: (performance.now()-t0).toFixed(0) })
              setUser({
                   id: decodedUser.id,
                   email: decodedUser.email,
                   role: decodedUser.role,
                   tier: decodedUser.tier ?? 'F1_0',
                   energyBalance: decodedUser.energyBalance ?? 0,
                   balanceUsd: decodedUser.balanceUsd ?? 0
                  })
            }
            setIsLoading(false)
            setReady(true)
            return;
          }

          // Токен есть но истёк → выкинуть из памяти, дальше идём на refresh как будто токена нет
          // Токен есть но истёк → выкинуть из памяти, дальше идём на refresh как будто токена нет
          if (existingToken && tokenExpired) {
            debugLog('AUTH', 'token expired, clearing before refresh', { runId })
            clearTokens()
          }

          // На публичных auth-страницах не дёргаем refresh — юзер пришёл логиниться,
          // refresh с протухшей cookie только плодит 401 и провоцирует CrowdSec.
          const AUTH_PAGES = ['/signin', '/signup', '/forgot-password', '/reset-password'];
          const pathname = typeof window !== 'undefined' ? window.location.pathname : '';
          if (AUTH_PAGES.some(p => pathname.includes(p))) {
            debugLog('AUTH', 'on auth page, skip refresh', { runId, pathname })
            setUser(null)
            setIsLoading(false)
            setReady(true)
            return;
          }

          let cancelled = false;
          refreshAccessToken()
          
            .then(async (data: RefreshResponse | null) => {
               if (cancelled) return;
               // console.log("[AuthContext] refreshAccessToken response:", data)
                debugLog('AUTH', 'refresh response', { runId, hasData: !!data, t: (performance.now()-t0).toFixed(0) })
                if (!data) {
                  clearTokens();
                  setUser(null);
                  if (isPrivatePath()) {
                    window.location.replace('/signin?reason=expired');
                  }
                  return
                }
                if (data.accessToken) {
                  setAccessToken(data.accessToken)
                  debugLog('AUTH', 'fetchUserFromApi start', { runId, t: (performance.now()-t0).toFixed(0) })
                  const apiUser = await fetchUserFromApi(data.accessToken)
                  if (cancelled) return;
                  if (apiUser) {
                    debugLog('AUTH', 'setUser from /me', { runId, userId: apiUser.id, t: (performance.now()-t0).toFixed(0) })
                    setUser({
                      id: apiUser.id,
                      email: apiUser.email,
                      role: apiUser.role,
                      tier: apiUser.tier ?? 'F1_0',
                      energyBalance: apiUser.energyBalance ?? 0,
                      balanceUsd: apiUser.balanceUsd ?? 0,
                    })
                  } else {
                    // fallback на JWT если /me недоступен
                    const decodedUser = getUserFromToken()
                    if (decodedUser) setUser({
                       ...decodedUser,
                       tier: decodedUser.tier ?? 'F1_0',
                        energyBalance: decodedUser.energyBalance ?? 0,
                        balanceUsd: decodedUser.balanceUsd ?? 0
                      })
                  }
                }
                
              })
            .catch((err) => {
              debugLog('AUTH', 'refresh failed', { runId, error: String(err) })
              clearTokens();
              setUser(null);
              if (isPrivatePath()) {
                window.location.replace('/signin?reason=expired');
              }
            })
            .finally(() => {
              if (!cancelled) {
                setIsLoading(false)
                setReady(true)
                debugLog('AUTH', 'ready', { runId, total: (performance.now()-t0).toFixed(0) })

              }
            })
            return () => {
            debugLog('AUTH', 'cleanup', { runId, t: (performance.now()-t0).toFixed(0) })
              cancelled = true;
              window.removeEventListener('pageshow', onPageShow);
            }
        }, 
        [])


  // 2. Ручное обновление данных пользователя (вызывается где угодно через useAuth)
  // Старый метод
  //const refreshUser = useCallback(async () => {
  // try {
      //const data: RefreshResponse | null = await refreshAccessToken()
   //   const data: RefreshResponse | null = await refreshAccessToken()
   //   if (data?.accessToken) {
   //     setAccessToken(data.accessToken)
   //     //setUser(data.user ?? null)
   //     const decodedUser = getUserFromToken()
   //     if (decodedUser) {
   //       setUser({ id: decodedUser.id, email: decodedUser.email, role: decodedUser.role, tier: decodedUser.tier ?? 'FREE', energyBalance: decodedUser.energyBalance ?? 0 })
   //     }
   //   }
  //  } catch {
      // Refresh не прошёл — оставляем текущего пользователя (или null)
  //  }
  //}, [])
  // Новый метод
  const refreshUser = useCallback(async () => {
  try {
    const token = getAccessToken()
    if (!token) return
    
    const res = await fetch(`${API_URL}/auth/me`, {
      headers: { Authorization: `Bearer ${token}` },
      credentials: "include",
    })
    
    if (!res.ok) return
    
    const data = await res.json()
    if (data) {
      setUser({
        id: data.id,
        email: data.email,
        role: data.role,
        tier: data.tier ?? 'FREE',
        energyBalance: data.energyBalance ?? 0,
        balanceUsd: data.balanceUsd ?? 0,
      })
    }
  } catch {
    // оставляем текущего пользователя
  }
}, [])


  // 3. Выход из системы
  // стало
    const logout = useCallback(async () => {
        const t0 = performance.now()
        debugLog('LOGOUT', 'start', {})
        const token = getAccessToken()
        try {
          // Свой Next.js route: проксирует на backend И сам гасит cookie с Domain=.salaria.app
          // (единственный способ убить HttpOnly cookie — Set-Cookie от того же origin/parent domain)
          await fetch(`/api/auth/logout`, {
            method: "POST",
            headers: token ? { Authorization: `Bearer ${token}` } : {},
            credentials: "include",
          })
          debugLog('LOGOUT', 'backend ok', { t: (performance.now()-t0).toFixed(0) })
        } catch (e) {
          debugLog('LOGOUT', 'backend failed', { error: String(e) })
        }
        clearTokens()
        setUser(null)
        try {
          const { useChatStore } = await import("@/store/chatStore")
          useChatStore.getState().setActiveChat(null)
          useChatStore.setState({ activeChatSessionId: null, sessions: [] })
        } catch {}
        try {
          localStorage.clear()
          sessionStorage.clear()
        } catch {}
        debugLog('LOGOUT', 'done', { t: (performance.now()-t0).toFixed(0) })
        // replace, чтобы Back не вернул на /chat/[id]
        window.location.replace("/signin")
      }, [])
      // Глобальный WS: слушаем payment:confirmed для любой страницы
        useEffect(() => {
          if (!user?.id) return
          const token = getAccessToken()
          if (!token) return
          const socket = io(API_URL, {
            transports: ["websocket"],
            auth: { token },
            reconnectionAttempts: 5,
            reconnectionDelay: 2000,
            reconnectionDelayMax: 30000,
          })
          socket.on("payment:confirmed", () => {
            refreshUser()
          })
          return () => { socket.disconnect() }
        }, [user?.id, refreshUser])

  // Пока не завершился initial refresh — ничего не рендерим
  // Это предотвращает флеш неавторизованного UI
  if (!ready) return (
  <div className="h-screen flex items-center justify-center">
    <div className="w-6 h-6 border-2 border-zinc-300 border-t-zinc-900 rounded-full animate-spin" />
  </div>
)

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        isLoading,
        logout,
        refreshUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider")
  }
  return context
}